import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Contacts as ContactsIcon,
} from '@mui/icons-material';
import { contactService, Contact } from '../services/contactService';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

export const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await contactService.getContacts({ top: 999 });
      const contacts = getApiCollection<Contact>(response, 'loading contacts');
      setContacts(contacts);
    } catch (err: any) {
      setError(err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = () => {
    setCreateDialogOpen(true);
  };

  const handleDeleteClick = (contact: Contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContact) return;

    try {
      await contactService.deleteContact(getProp(selectedContact, 'id'));
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      loadContacts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact');
    }
  };

  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;

    const search = searchTerm.toLowerCase();
    return contacts.filter((contact: any) => {
      const displayName = getProp(contact, 'displayName')?.toLowerCase() || '';
      const emailAddresses = getProp(contact, 'emailAddresses');
      const email = emailAddresses?.[0]?.address?.toLowerCase() || '';
      const companyName = getProp(contact, 'companyName')?.toLowerCase() || '';

      return displayName.includes(search) || email.includes(search) || companyName.includes(search);
    });
  }, [contacts, searchTerm]);

  const paginatedContacts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredContacts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredContacts, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContactsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Contacts
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateContact}>
          New Contact
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={styles.filterSection}>
        <TextField
          placeholder="Search contacts..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <Typography variant="body2" color="text.secondary">
          {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Display Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Email Address</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Company</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Job Title</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Phone</TableCell>
              <TableCell sx={styles.tableHeaderCell} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedContacts.map((contact: any) => {
              const id = getProp(contact, 'id');
              const displayName = getProp(contact, 'displayName');
              const emailAddresses = getProp(contact, 'emailAddresses');
              const email = emailAddresses?.[0]?.address || '';
              const companyName = getProp(contact, 'companyName');
              const jobTitle = getProp(contact, 'jobTitle');
              const businessPhones = getProp(contact, 'businessPhones');
              const mobilePhone = getProp(contact, 'mobilePhone');
              const phone = businessPhones?.[0] || mobilePhone || '';

              return (
                <TableRow key={id} hover>
                  <TableCell>{displayName}</TableCell>
                  <TableCell>{email}</TableCell>
                  <TableCell>{companyName || '-'}</TableCell>
                  <TableCell>{jobTitle || '-'}</TableCell>
                  <TableCell>{phone || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(contact)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {paginatedContacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No contacts were returned. If you expected contacts, verify the API permissions
                    or current search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredContacts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Create Contact Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Contact creation functionality will be implemented here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the contact "
            {selectedContact ? getProp(selectedContact, 'displayName') : ''}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
